import {
  Button,
  Column,
  Columns,
  FlexBox,
  IconButton,
  Text,
} from '@dixa/lib-shared-habla';
import { InstrumentPanel } from 'lib-analytics-types';
import { AnalyticsMenu, type AnalyticsMenuActions } from 'lib-analytics-ui';
import { ampli } from 'lib-shared-tracking';
import pick from 'lodash/pick';
import { useCallback, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';

import { appActions } from 'app/app.actions';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useShortHumanReadableTimer } from 'utils/useShortHumanReadableTimer';

import { analyticsActions } from '../../analytics.actions';
import { AnalyticsControls } from '../../components/controls/analyticsControls';
import { AnalyticsDocumentationButton } from '../../components/documentation/analyticsDocumentationButton';
import { AnalyticsHeader } from '../../components/header/analyticsHeader';
import { AnalyticsPanels } from '../../components/panels/analyticsPanels';

import { useDashboardInit } from './utils/useDashboardInit/useDashboardInit';

export const DashboardView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const mainContentRef = useRef<HTMLDivElement | null>(null);
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const isMediumScreen = useBreakpointValue({ base: true, lg: false });
  const flags = useFeatureFlag(['analytics-custom-dashboards']);

  const { dashboard, isInitialized } = useDashboardInit();
  const state = useAppSelector((state) => state.analytics);

  // Sends a tracking event when a dashboard is loaded or changed.
  useEffect(() => {
    if (dashboard) {
      ampli.analyticsDashboardViewed({ name: dashboard.title });

      // Scroll to the top when the dashboard changes.
      if (mainContentRef.current) {
        mainContentRef.current.scrollTop = 0;
      }
    }
  }, [dashboard]);

  /* The relative time of the last time the dashboard data was refreshed. */
  const lastRefreshTime = useShortHumanReadableTimer(state.meta.lastRefresh);

  /* The list of actions available for this view. */
  const actions: AnalyticsMenuActions = [
    {
      type: 'button',
      icon: 'sync',
      label: 'Refresh data',
      description: `Last refreshed ${lastRefreshTime}`,
      disabled: false,
      onClick: () => {
        dispatch(analyticsActions.forceRefresh());

        if (dashboard) {
          ampli.analyticsRefreshClicked({ dashboard: dashboard.title });
        }
      },
    },
  ];

  const handleCreateQuickView = useCallback(async () => {
    if (!dashboard) return;

    const { quickView } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'analytics:quickView',
          arg: {
            mode: 'create',
            panels:
              dashboard.panels.map((panel) => ({
                ...panel,
                controls: {
                  ...pick(state, ['period', 'filters', 'settings']),
                  context: state.context[dashboard.id],
                },
              })) ?? [],
          },
        },
      }),
    );

    navigate({
      path: '/analytics/dashboard/custom/:id',
      params: {
        path: {
          id: quickView.id,
        },
      },
    });
  }, [dashboard, dispatch, navigate, state]);

  const handlePinPanelToQuickView = useCallback(
    async (panel: InstrumentPanel) => {
      if (!dashboard) return;

      try {
        await dispatch(
          appActions.showModal({
            modal: {
              type: 'analytics:pinPanel',
              arg: {
                panel: {
                  ...panel,
                  controls: {
                    ...pick(state, ['period', 'filters', 'settings']),
                    ...panel.controls,
                    context: state.context[dashboard.id],
                  },
                  dashboard_id: dashboard.id,
                },
              },
            },
          }),
        );

        ampli.analyticsPinPanelClicked({
          dashboard: dashboard.title,
          panel: panel.title,
        });
      } catch (_error) {}
    },
    [dashboard, dispatch, state],
  );

  return (
    <Columns>
      <Column>
        <Column.Row slottable borderBottom>
          <Column.Slot>
            <AnalyticsHeader tier={dashboard?.tier}>
              {dashboard && (
                <>
                  {!isMediumScreen &&
                    dashboard.group &&
                    dashboard.group !== dashboard.title && (
                      <Text.Heading color="tertiary" as="h2">
                        {dashboard.group}
                      </Text.Heading>
                    )}

                  <Text.Heading color="primary" wrap="nowrap" as="h2">
                    {dashboard.title} dashboard
                  </Text.Heading>

                  <AnalyticsDocumentationButton
                    type="dashboard"
                    dashboard={dashboard}
                  />
                </>
              )}
            </AnalyticsHeader>
          </Column.Slot>

          <Column.Slot noVerticalPadding placement="end">
            <FlexBox gap="200">
              {isSmallScreen ? (
                <IconButton
                  icon="bookmark"
                  variant="secondary"
                  onClick={handleCreateQuickView}
                />
              ) : (
                <Button variant="secondary" onClick={handleCreateQuickView}>
                  Save dashboard
                </Button>
              )}

              <AnalyticsMenu actions={actions} />
            </FlexBox>
          </Column.Slot>
        </Column.Row>

        <Column.Row size={1} scrollable ref={mainContentRef}>
          {isInitialized && (
            <>
              <FlexBox flexDirection="column" gap="300">
                <AnalyticsControls initiallyCollapsedFilters={isSmallScreen} />

                <AnalyticsPanels
                  panels={dashboard!.panels}
                  {...(flags.analyticsCustomDashboards && {
                    onPin: handlePinPanelToQuickView,
                  })}
                />
              </FlexBox>

              <Outlet />
            </>
          )}
        </Column.Row>
      </Column>
    </Columns>
  );
};
